import { slateToHtml } from "@slate-serializers/html";
import { Element as DomElement, Text as DomText } from "domhandler";
import { marked } from "marked";
import {
  Descendant,
  Editor,
  Range,
  Element as SlateElement,
  Text as SlateText,
  Transforms,
} from "slate";
import { jsx } from "slate-hyperscript";
import TurndownService from "turndown";
import { CustomAncestor, MentionElement } from "./editortypes";
import { isBlockActive, toggleBlock } from "./SlateComponents/SlateToolbar";

const turndownService = new TurndownService({
  headingStyle: "atx", // Use ATX-style headings (e.g., # for h1),
});

const TEXT_TAGS = {
  CODE: () => ({ code: true }),
  EM: () => ({ italic: true }),
  STRONG: () => ({ bold: true }),
};

const ELEMENT_TAGS = {
  A: (el) => ({ type: "link", url: el.getAttribute("href") }),
  BLOCKQUOTE: () => ({ type: "blockquote" }),
  PRE: () => ({ type: "code" }),
  H1: () => ({ type: "h1" }),
  H2: () => ({ type: "h2" }),
  H3: () => ({ type: "h3" }),
  IMG: (el) => ({ type: "image", url: el.getAttribute("src"), style:el.style }),
  LI: () => ({ type: "list-item" }),
  OL: () => ({ type: "numbered-list" }),
  P: () => ({ type: "paragraph" }),
  UL: () => ({ type: "bulleted-list" }),
  SCOPED: (el) => ({ type: "scoped-text", id: el.getAttribute("id"), indexid:el.getAttribute("indexid"), count:el.getAttribute("count"), removeTextScoped: el.removeTextScoped }),
};

const parseHtmlFromString = (html: string) => {
  return new DOMParser().parseFromString(html, "text/html");
};

const emptyText = [{ text: "", scoped:null }];
const emptySlate = [{ type: "paragraph", children: emptyText }];

// An emphasis tag can legitimately wrap an element: marked's GFM autolinker turns a
// bolded email, www host or URL into <strong><a>, and slate-hyperscript's <text>
// rejects anything but text. Carry the mark down to the element's own text rather
// than handing the element to jsx("text"), which throws and - since markdownToSlate
// runs during render for every comment in a feed - takes the whole feed with it.
const applyTextMarks = (node, attrs) => {
  if (typeof node === "string" || SlateText.isText(node)) return jsx("text", attrs, node);
  if (!node?.children) return node;

  return { ...node, children: node.children.map((child) => applyTextMarks(child, attrs)).flat() };
};

export const deserialize = (el, isScoped:boolean) => {
  if (el.nodeType === 3) {
    let text = el.textContent;

    return text ? { text } : null;
  }

  if (el.nodeType !== 1) return null;
  else if (el.nodeName === "BR") {
    return "\n";
  }

  let parent = el;

  if (el.nodeName === "PRE" && el.firstChild?.nodeName === "CODE") {
    parent = el.firstChild;
  }

  let children = Array.from(parent.childNodes)
    .map(e => deserialize(e, isScoped || el.nodeName === "SCOPED"))
    .flat()
    .filter(Boolean); // Remove null values

  // Cleanup: Remove trailing line breaks from lists
  children = children.map((child) => {
    if (child?.type === "numbered-list" || child?.type === "bulleted-list") {
      return {
        ...child,
        children: child.children.filter((c) => c?.text !== "\n"),
      };
    }
    return child;
  });

  if (children.length === 0) children = [{ text: isScoped ? " " : "" }];

  if (el.nodeName === "BODY") return jsx("fragment", {}, children);

  if (el.nodeName=="SCOPED"){
    return jsx("element", ELEMENT_TAGS[el.nodeName](el), children);
  }

  if (ELEMENT_TAGS[el.nodeName]) {
    return jsx("element", ELEMENT_TAGS[el.nodeName](el), children);
  }

  if (TEXT_TAGS[el.nodeName]) {
    const attrs = TEXT_TAGS[el.nodeName](el);
    return children.map((child) => applyTextMarks(child, attrs));
  }
  return children;
};

const htmlToMarkdown = (html) => {
  const markdown = turndownService.turndown(html);

  return markdown;
};

export const HOTKEYS = {
  "mod+b": "bold",
  "mod+i": "italic",
  "mod+u": "underline",
  "mod+`": "code",
  "mod+d": "strikethrough",
};

export const withBullets = (editor) => {
  const { insertBreak } = editor;

  editor.insertBreak = () => {
    const { selection } = editor;

    if (selection && Range.isCollapsed(selection)) {
      const [start] = Range.edges(selection);
      const startNode = Editor.node(editor, start);

      if (Editor.isBlock(editor, startNode[0])) {
        const [parentNode, parentPath] = Editor.parent(editor, start.path);
        const pNode = parentNode as CustomAncestor;
        if (pNode.type === "list-item") {
          const newListItem = { type: "list-item", children: emptyText };
          Transforms.insertNodes(editor, newListItem, { at: parentPath });
          return;
        }
      }
    }

    insertBreak();
  };

  return editor;
};

export const withHtml = (editor) => {
  const { insertData, isInline, isVoid } = editor;

  editor.isInline = (element) => element.type === "link" || isInline(element);
  editor.isVoid = (element) => element.type === "image" || isVoid(element);

  editor.insertData = (data) => {
    const html = data.getData("text/html");
    if (html) {
      let fragment: Descendant[] = markdownToSlate(
        htmlToMarkdown(parseHtmlFromString(html).body)
      );
      const firstChildType = fragment[0].type;
      if (!isBlockActive(editor, firstChildType)) {
        toggleBlock(editor, firstChildType);
      }

      Transforms.insertFragment(editor, fragment);
      Transforms.select(editor, Editor.end(editor, []));
      return;
    }
    insertData(data);
  };

  return editor;
};

export const insertMention = (
  editor,
  character,
  email,
  firstMention?: boolean
) => {
  const mention: MentionElement = {
    type: "mention",
    character,
    email,
    children: [{ text: character, scoped:null }],
  };
  if (firstMention) {
    removeFirstCharacter(editor);
  }
  Transforms.insertNodes(editor, mention as any);
  Transforms.move(editor);
};

export const withMentions = (editor) => {
  const { isInline, isVoid, markableVoid } = editor;

  editor.isInline = (element) =>
    element.type === "mention" || isInline(element);
  editor.isVoid = (element) => element.type === "mention" || isVoid(element);
  editor.markableVoid = (element) =>
    element.type === "mention" || markableVoid(element);

  return editor;
};

export const isMarkActive = (editor, format) => {
  const marks = Editor.marks(editor);
  return marks ? marks[format] === true : false;
};

export const toggleMark = (editor, format) => {
  const isActive = isMarkActive(editor, format);

  if (isActive) {
    Editor.removeMark(editor, format);
  } else {
    Editor.addMark(editor, format, true);
  }
};

export const deepEqual = (obj1: any, obj2: any): boolean => {
  if (obj1 === obj2) {
    return true;
  }

  if (
    typeof obj1 !== "object" ||
    obj1 === null ||
    typeof obj2 !== "object" ||
    obj2 === null
  ) {
    return false;
  }

  const keys1 = Object.keys(obj1);
  const keys2 = Object.keys(obj2);

  if (keys1.length !== keys2.length) {
    return false;
  }

  for (const key of keys1) {
    if (!keys2.includes(key) || !deepEqual(obj1[key], obj2[key])) {
      return false;
    }
  }

  return true;
};

export const removeFirstCharacter = (editor) => {
  const firstNode = editor.children[0];
  if (SlateText.isText(firstNode.children[0])) {
    const text = firstNode.children[0].text;
    if (text.length > 0) {
      Transforms.delete(editor, {
        at: { path: [0, 0], offset: 0 },
        distance: 1,
        unit: "character",
      });
    }
  }
};

export const imageStyle: (style: any) => React.CSSProperties = (style) => ({  
  display: 'block',
  width: style ? style.width : "auto",
  height: style ? style.height : "auto",
  float: style ? style.float : "none",
  margin: '0', // Default center
  marginLeft: 'auto', marginRight: 'auto'
});

export const marksToScopedElements = (nodes: Descendant[]): Descendant[] => {
  const stripInvisible = (s: string = '') => s.replace(/[\u200B\uFEFF]/g, '');

  const convert = (node: any): any => {
    // text leaf
    if (SlateText.isText(node)) {
      const text = stripInvisible(node.text ?? '');
      if (node.scoped) {
        const { scoped, ...rest } = node;
        return {
          type: 'scoped-text',
          id: String(scoped.id ?? ''),
          indexid: String(scoped.indexid ?? '0'),
          count: String(scoped.count ?? '1'),
          children: [{ ...rest, text }]
        };
      }
      return { ...node, text };
    }

    // element node (block/inline) -> recurse children
    if (node.children && Array.isArray(node.children)) {
      // convert children first
      const children = node.children.map(convert).filter(Boolean);

      // If this node is already a scoped-text element, sanitize and flatten nested scoped-text children with same id
      if (node.type === 'scoped-text') {
        const id = String(node.id ?? '');
        const indexid = String(node.indexid ?? '0');
        const count = String(node.count ?? '1');
        const flattened: any[] = [];
        for (const ch of children) {
          if (ch?.type === 'scoped-text' && String(ch.id) === id && String(ch.indexid) === indexid && String(ch.count) === count) {
            // integrate child's children (flatten)
            flattened.push(...(ch.children || []));
          } else {
            flattened.push(ch);
          }
        }
        return { ...node, children: flattened };
      }

      return { ...node, children };
    }

    return node;
  };

  // initial conversion
  let converted = nodes.map(convert);

  // second pass: merge adjacent scoped-text siblings with same id/indexid/count
  const mergeAdjacentScoped = (node: any): any => {
    if (!node?.children || !Array.isArray(node.children)) return node;
    const out: any[] = [];
    for (const ch of node.children) {
      const mergedChild = mergeAdjacentScoped(ch);
      if (
        out.length > 0 &&
        mergedChild?.type === 'scoped-text' &&
        out[out.length - 1].type === 'scoped-text' &&
        out[out.length - 1].id === mergedChild.id &&
        out[out.length - 1].indexid === mergedChild.indexid &&
        out[out.length - 1].count === mergedChild.count
      ) {
        // merge children arrays
        out[out.length - 1] = {
          ...out[out.length - 1],
          children: [...(out[out.length - 1].children || []), ...(mergedChild.children || [])]
        };
      } else {
        out.push(mergedChild);
      }
    }
    return { ...node, children: out };
  };

  converted = converted.map(mergeAdjacentScoped);

  return converted;
};



export const htmlParser = (data) => {
  data = marksToScopedElements(data);
  const toCss = (obj: Record<string, any>) =>
          Object.entries(obj)
            .filter(([, v]) => v !== undefined && v !== null && v !== '')
            .map(([k, v]) => {
              // convert camelCase to kebab-case
              const prop = k.replace(/([A-Z])/g, '-$1').toLowerCase();
              // append px for numeric width/height if value is a number or numeric string without unit
              if ((prop === 'width' || prop === 'height' || prop.endsWith('margin') || prop.endsWith('left') || prop.endsWith('right')) &&
                  (typeof v === 'number' || (/^\d+$/.test(String(v))))) {
                return `${prop}: ${v}px`;
              }
              return `${prop}: ${v}`;
            })
            .join('; ');
  return slateToHtml(data, {
    elementMap: {
      paragraph: "p",
      "bulleted-list": "ul",
      "list-item": "li",
      "numbered-list": "ol",
      "scoped-text": "span"
    },
    elementTransforms: {
      image: ({ node }) => {
        const containerStyle = toCss({ display: "block" });
        const imgStyle = toCss(imageStyle(node.style) || {});
        const imgEl = new DomElement("img", { src: node.url, style: imgStyle });
        return new DomElement("div", { style: containerStyle }, [imgEl]);
      },

      link: ({ node, children }) => {
        return new DomElement("a", { href: node.url }, children);
      },
      paragraph: ({ children }) => {
        if (children.every(c => c["children"]?.[0]?.data === "")) {
          return new DomElement("br", {});
        }
        return new DomElement("p", {}, children);
      },
      "numbered-list": ({ children }) => {
        children = children.filter(node => (node as any).name === "li");
        if (children.length === 0) {
          return new DomElement("br", {});
        }
        return new DomElement("ol", {}, children);
      },
      "bulleted-list": ({ children }) => {
        children = children.filter(node => (node as any).name === "li");
        if (children.length === 0) {
          return new DomElement("br", {});
        }
        return new DomElement("ul", {}, children);
      },
      "list-item": ({ children }) => {
        return new DomElement("li", {}, children);
      },
      h1: ({ children }) => {
        if (children.every(c => c["children"]?.[0]?.data === "")) {
          return new DomElement("br", {});
        }
        return new DomElement("h1", {}, children);
      },
      h2: ({ children }) => {
        if (children.every(c => c["children"]?.[0]?.data === "")) {
          return new DomElement("br", {});
        }
        return new DomElement("h2", {}, children);
      },
      h3: ({ children }) => {
        if (children.every(c => c["children"]?.[0]?.data === "")) {
          return new DomElement("br", {});
        }
        return new DomElement("h3", {}, children);
      },
      mention: ({ node, children }) => {
        if (children.every(c => c["children"]?.[0]?.data === "")) {
          return new DomElement("br", {});
        }
        return new DomElement("a", { href: "mention:" + node.email }, children);
      },
      "scoped-text": ({node, children }) => {
         const content = !children.every(c => c["children"][0]?.data === "")
            ? children
            : [new DomText(" ")];
        return new DomElement("scoped", {id:node.id.toString(), type: node.type, indexid:node.indexid, count:node.count}, content);
      }
    },
    convertLineBreakToBr: true,
    markMap: {
      bold: ["strong"],
      italic: ["em"],
      underline: ["u"],
      strikethrough: ["s"],
      image: ["img"],
      code: ["code"],
    },
  });
};

export const slateToMd = (data) => {
  const turndownService = new TurndownService({
    headingStyle: "atx", // Use ATX-style headings (e.g., # for h1),
  });

  turndownService.addRule("lineBreakToLiteralNewline", {
    filter: "br",
    replacement: function () {
      return "\\\n"; // double backslash for literal \n in string
    },
  });

  turndownService.addRule("ScopedText", {
    filter: (node) => {
      return  node.nodeName === "SPAN" && node.hasAttribute("type") && node.getAttribute("type")==="scoped-text";
    },
    replacement: function (content,e:any) {
      return `[scopedId:${e.getAttribute("id")}?${content}]`; // double backslash for literal \n in string
    },
  });

  turndownService.addRule("lineBreakToLiteralNewline", {
    filter: function (node) {
      return node.nodeName === "OL" || node.nodeName === "UL";
    },
    replacement: (content) => {
      return `${content}\n`;
    },
  });

  turndownService.addRule("brAfterSpecificElement", {
    filter: function (node) {
      return (
        node.nodeName === "BR" &&
        node.previousSibling &&
        (node.previousSibling.nodeName === "OL" ||
          node.previousSibling.nodeName === "UL")
      );
    },
    replacement: function () {
      return "\n"; // Your custom replacement
    },
  });

  turndownService.addRule("brAfterSpecificElement", {
    filter: function (node) {
      return (
        node.nodeName === "BR" &&
        node.nextSibling &&
        (node.nextSibling.nodeName === "OL" ||
          node.nextSibling.nodeName === "UL")
      );
    },
    replacement: function () {
      return "\n"; // Your custom replacement
    },
  });

  turndownService.addRule("removeExtraNewLines", {
    filter: ["p"],
    replacement: (content) => {
      return `${content}\n`;
    },
  });

  let td = turndownService.turndown(htmlParser(data));
  td = td.replace(/\\(?![n\\])/g, '');
  return td;
};


// List containers (ol/ul) may only contain list-item children. Injecting bare
// text leaves into them produces stray empty lines the user can't delete without
// collapsing the whole list, so they must be excluded from the wrapping below.
const BLOCK_CONTAINER_TYPES = new Set(["numbered-list", "bulleted-list"]);

const isListBlock = (node: any): boolean =>
  BLOCK_CONTAINER_TYPES.has(node?.type);

const isEmptyParagraph = (node: any): boolean =>
  node?.type === "paragraph" &&
  Array.isArray(node.children) &&
  node.children.length === 1 &&
  node.children[0]?.text === "";

const ensureTextSiblings = (node: any): any => {
  if (!node.children) return node;
  let kids = node.children.map(ensureTextSiblings);
  if (BLOCK_CONTAINER_TYPES.has(node.type)) {
    return { ...node, children: kids };
  }
  // If a block/inline element has no text node children (only inlines/voids like links or images),
  // Slate can't place a cursor inside it — add empty text nodes around them.
  const hasOnlyElements = kids.length > 0 && kids.every((k) => k.text === undefined);
  if (hasOnlyElements) {
    return { ...node, children: [{ text: "" }, ...kids, { text: "" }] };
  }
  return { ...node, children: kids };
};

// A paragraph that carries newlines inside its text leaves renders as one
// collapsed block, so the caret can't move line-to-line with the arrow keys.
// Split it into one paragraph block per line so vertical navigation works and
// the structure matches how the lines were originally authored (Enter = block).
// This round-trips to the same markdown on save, so stored content is unchanged.
const splitParagraphOnNewline = (node: any): any[] => {
  if (node?.type !== "paragraph" || !node.children) return [node];
  const hasNewline = node.children.some(
    (c) => typeof c?.text === "string" && c.text.includes("\n")
  );
  if (!hasNewline) return [node];

  const groups: any[][] = [[]];
  for (const child of node.children) {
    if (typeof child?.text === "string" && child.text.includes("\n")) {
      const parts = child.text.split("\n");
      parts.forEach((part, i) => {
        if (i > 0) groups.push([]);
        if (part !== "") groups[groups.length - 1].push({ ...child, text: part });
      });
    } else {
      groups[groups.length - 1].push(child);
    }
  }
  return groups.map((kids) => ({
    ...node,
    children: kids.length ? kids : [{ text: "" }],
  }));
};

export const markdownToSlate = (_markdown): Descendant[] => {
  if (!_markdown || !_markdown.trim()) {
    return emptySlate;
  }

  // Convert Markdown to HTML
  const html = marked(_markdown, {
    async: false,
  });
  const doc = parseHtmlFromString(html);
  let children = deserialize(doc.body, false);
  children = children.map((child) => {
    if (child.text === "\n") {
      return { type: "paragraph", children: [{ text: "" }] };
    }
    return child;
  });
  children = children.filter((child, i) => {
    if (!isEmptyParagraph(child)) return true;
    return !isListBlock(children[i - 1]) && !isListBlock(children[i + 1]);
  });
  children = children.flatMap(splitParagraphOnNewline);
  children = children.map(ensureTextSiblings);
  return children.length ? children : emptySlate;
};

// iOS Safari's `beforeinput` for the space that confirms an autocorrect
// suggestion is not always reported as a plain `insertText`. WebKit can
// instead report it as `insertParagraph`/`insertLineBreak` — Slate's default
// handling for those types unconditionally calls `Editor.insertBreak`, which
// is exactly the unwanted new line/caret jump.
//
// Two variants of this have been observed, so both signals are checked:
//   1. iOS reports the *entire* "replace word + confirm" gesture as one
//      insertParagraph/insertLineBreak event, whose target range still
//      covers the misspelled word (a real Enter's range is always
//      collapsed at the bare caret) — `targetRange.collapsed === false`.
//   2. iOS reports the replacement as its own earlier insertReplacementText
//      event (already applied by the time this one fires), so this event's
//      range comes through collapsed — the caller passes `justAutocorrected`
//      (set when it handled that earlier event) to catch this case too.
export const isAutocorrectMisreportedAsBreak = (
  inputType: string,
  targetRange?: { collapsed: boolean } | null,
  justAutocorrected?: boolean
): boolean => {
  if (inputType !== "insertParagraph" && inputType !== "insertLineBreak") {
    return false;
  }
  if (justAutocorrected) return true;
  return !!targetRange && targetRange.collapsed === false;
};

// A beforeinput event's replacement text isn't always on `event.data` — per
// the Input Events spec (and slate-react's own default handling, which reads
// `event.dataTransfer || event.data`), WebKit can deliver it via
// `event.dataTransfer` instead. Missing this was the bug: a caller that only
// checks `event.data` finds nothing, but has already selected the misspelled
// word expecting to replace it — inserting anything into that selection
// afterward (even a single space) deletes the word instead of correcting it.
export const extractReplacementText = (event: {
  data?: string | null;
  dataTransfer?: { getData: (format: string) => string } | null;
}): string | undefined => {
  if (typeof event.data === "string" && event.data !== "") {
    return event.data;
  }
  const fromTransfer = event.dataTransfer?.getData("text/plain");
  return fromTransfer || undefined;
};

// Slate text leaves render with white-space: pre-wrap (SlateEl.tsx), so a
// literal "\n" inside a leaf's text shows up as a real visible line break
// even though no new paragraph block was ever created. On some iOS versions
// the DOM text left behind by the autocorrect-confirming keystroke contains
// one of these instead of a plain space; collapse it back down to the space
// the user actually typed.
export const sanitizeAutocorrectedText = (text: string): string =>
  text.replace(/\r\n|\r|\n/g, " ");

export const ensureParagraphOnEmpty = (editor: Editor) => {
  const { selection } = editor;

  if (selection && Editor.string(editor, selection).trim() === "") {
    const [match] = Editor.nodes(editor, {
      match: (n) => SlateElement.isElement(n),
    });

    if (match) {
      const [node] = match;

      // If block is not already a paragraph, change it
      if (node["type"] !== "paragraph") {
        Transforms.setNodes(editor, { type: "paragraph" });
      }
    }
  }
};
